import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { FadeIn } from '@atlaskit/motion';
import { fg as platform_fg } from '@atlaskit/platform-feature-flags';
import Portal from '@atlaskit/portal';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	UNSAFE_UpdateOnChange as PopoverUpdateOnChange,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	type Placement,
} from '@atlaskit/spotlight';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { fg } from '@confluence/feature-gating';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import CreateWithRovoSpotlightAnimationDark from '../assets/CreateWithRovoSpotlightAnimationDark.gif';
import CreateWithRovoSpotlightAnimationLight from '../assets/CreateWithRovoSpotlightAnimationLight.gif';

import { useIsAIFCOnbardingEligible } from './hooks/useIsAIFCOnbardingEligible.ts';

const styles = cssMap({
	image: {
		width: '295px',
		height: '135px',
		backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
	},
});

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.title',
		defaultMessage: 'Start work faster with Rovo',
		description: 'Title for smart create with AI spotlight',
	},
	smartCreateDescription: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.description',
		defaultMessage: 'Let Rovo draft the doc or whiteboard you need to get started fast.',
		description: 'Description text for smart create with AI spotlight',
	},
	smartCreateCTA: {
		id: 'onboarding-ai-first-create.nudge.smart-create-with-ai.cta',
		defaultMessage: 'Try it now',
		description: 'CTA button text for smart create with AI spotlight',
	},
});

export type AISmartCreateSpotlightProps = {
	children?: React.ReactNode;
	onClick?: (e: any) => void;
	isLoading?: boolean;
	source: string;
	offset?: [number | null | undefined, number | null | undefined];
	placement?: Placement;
};

export const AISmartCreateSpotlight = ({
	children,
	onClick,
	source,
	isLoading,
	offset = [0, 2],
	placement = 'left-start',
}: AISmartCreateSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { useChoreographer } = useChoreographerAssets();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const handleSpotlightStart = useCallback(() => {
		const statesToSet = [
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIFCSpotlight,
				value: 'true',
			},
			{
				key: AIFC_ONBOARDING_STATE_KEYS.hasSeenDeeplinkReturnSpotlight,
				value: 'true',
			},
		];

		// Write to onboarding state (server-side persistence)
		setOnboardingState(statesToSet);

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AISmartCreateSpotlight',
				attributes: {
					source,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, setOnboardingState, source]);

	const handleSpotlightStop = () => {
		setIsSpotlightActive(false);
	};

	const choreographer = useChoreographer('AISmartCreateSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const {
		isAIFCOnbardingEligible: isChangboardingEligible,
		loading: isChangboardingEligibleLoading,
		error: isChangboardingEligibleError,
	} = useIsAIFCOnbardingEligible();

	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { hasSeenAIFCSpotlight } = deserializeState(onboardingStateData);

	const handleSpotlightInteraction = useCallback(
		(e: any) => {
			onClick?.(e);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'AISmartCreateSpotlightCTA',
					source,
				},
			}).fire();

			void choreographer.stopMessage();
		},
		[choreographer, createAnalyticsEvent, onClick, source],
	);

	const handleOnClick = useCallback(
		(e: any) => {
			handleSpotlightInteraction(e);
		},
		[handleSpotlightInteraction],
	);

	const handleOnDismiss = useCallback(() => {
		setIsSpotlightActive(false);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AISmartCreateSpotlightDismiss',
				source,
			},
		}).fire();

		void choreographer.stopMessage();
	}, [choreographer, createAnalyticsEvent, source]);

	const getLocalOverrideValue = useCallback((): boolean => {
		try {
			if (sessionStorage.getItem(keys.AIFC_CREATE_SPOTLIGHT_SESSION_KEY)) {
				return true;
			}
		} catch {
			return false;
		}
		return false;
	}, []);

	useEffect(() => {
		const localOverride = getLocalOverrideValue();
		const loadingComplete =
			!isLoading &&
			!isChangboardingEligibleLoading &&
			!isChangboardingEligibleError &&
			!isOnboardingStateLoading &&
			!isOnboardingStateError;

		if (loadingComplete && isChangboardingEligible && !hasSeenAIFCSpotlight) {
			void choreographer.startMessage();
		} else if (localOverride) {
			handleSpotlightStart();
			sessionStorage.removeItem(keys.AIFC_CREATE_SPOTLIGHT_SESSION_KEY);
		}
	}, [
		choreographer,
		hasSeenAIFCSpotlight,
		isLoading,
		isChangboardingEligible,
		isChangboardingEligibleError,
		isChangboardingEligibleLoading,
		isOnboardingStateError,
		isOnboardingStateLoading,
		getLocalOverrideValue,
		handleSpotlightStart,
	]);

	const CreateWithRovoSpotlightMedia = useMemo(() => {
		return (
			<div css={styles.image}>
				<Image
					src={CreateWithRovoSpotlightAnimationLight}
					srcDark={CreateWithRovoSpotlightAnimationDark}
					width={295}
					height={135}
					alt=""
				/>
			</div>
		);
	}, []);

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange />
			<PopoverTarget>{children}</PopoverTarget>
			<Portal zIndex={600}>
				<PopoverContent
					dismiss={handleOnDismiss}
					isVisible={isSpotlightActive}
					placement={placement}
					offset={offset}
				>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId="ai-smart-create-spotlight">
								<SpotlightHeader>
									<SpotlightHeadline>{formatMessage(i18n.smartCreateTitle)}</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>{CreateWithRovoSpotlightMedia}</SpotlightMedia>
								<SpotlightBody>
									<Text>{formatMessage(i18n.smartCreateDescription)}</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={(e) => handleOnClick(e)}>
											{formatMessage(i18n.smartCreateCTA)}
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						return platform_fg('platform-dst-motion-uplift-spotlight') ? (
							spotlightCard
						) : (
							<FadeIn entranceDirection={placement === 'right-end' ? 'right' : 'left'}>
								{(props) => <div {...props}>{spotlightCard}</div>}
							</FadeIn>
						);
					})()}
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	);
};
